import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { Show } from "@clerk/react";
import { Link } from "react-router-dom";
import { HeartHandshake, BarChart3, Trophy, Coins } from "lucide-react";
import { makeApi } from "@/lib/api";
import { useT } from "@/i18n";
import ModulesGrid from "@/components/ModulesGrid";
import ParentsLearn from "@/components/ParentsLearn";
import FamilyPlan from "@/components/FamilyPlan";
import CoinRewards from "@/components/CoinRewards";
import Badges from "@/components/Badges";
import Gallery from "@/components/ailab/Gallery";

export default function Parents() {
  const { t, lang } = useT();
  const { getToken, isSignedIn } = useAuth();
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    if (!isSignedIn) return;
    const api = makeApi(getToken);
    api.get("/parent/overview").then((r) => setOverview(r.data)).catch(() => {});
  }, [getToken, isSignedIn]);

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center gap-3">
          <HeartHandshake className="w-8 h-8 text-pink-500" />
          <h1 className="font-fredoka text-4xl sm:text-5xl font-bold text-slate-900">{t.parents.title}</h1>
        </div>
        <p className="text-slate-700 mt-2 max-w-2xl">{t.parents.sub}</p>

        <ParentsLearn />

        <FamilyPlan />
        <div className="mt-8"><CoinRewards parent onChange={() => window.dispatchEvent(new Event("coins-changed"))} /></div>

        <section className="learn-bg rounded-[2rem] border-4 border-white shadow-xl p-4 sm:p-8 mt-8" data-testid="parents-learn-section">
          <div className="text-center">
            <h2 className="font-fredoka text-3xl sm:text-5xl font-bold leading-tight" data-testid="parents-learn-title">
              <span className="bubble-text text-white">{t.learnA} </span>
              <span className="bubble-text text-yellow-300">{t.learnB}</span>
              <span className="bubble-text text-white">{t.learnC === "?" ? "?" : ` ${t.learnC}`}</span>
            </h2>
            <p className="inline-block mt-4 bg-white/90 rounded-full px-5 py-2 text-sm sm:text-lg font-bold text-blue-900 shadow-lg">
              {t.learnSub}
            </p>
          </div>
          <div className="mt-8">
            <ModulesGrid />
          </div>
        </section>

        <Show when="signed-out">
          <div className="mt-8 bg-amber-100 border-4 border-slate-900 chunky-shadow-sm rounded-3xl p-6">
            <p className="font-bold text-slate-900">
              {lang === "es"
                ? "Entra con tu cuenta para ver el progreso."
                : "Sign in to see progress."}
            </p>
            <Link
              to="/sign-in"
              className="press-btn inline-block mt-3 bg-sky-400 border-2 border-slate-900 chunky-shadow-sm rounded-full px-5 py-2 font-bold"
              data-testid="parent-signin-btn"
            >
              {t.nav.login}
            </Link>
          </div>
        </Show>

        <Show when="signed-in">
          {overview && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
                <StatCard icon={Coins} color="bg-yellow-200" label={t.parents.coinsEarned} value={overview.coins} />
                <StatCard icon={Trophy} color="bg-green-200" label={t.parents.lessonsDone} value={overview.total_lessons_done} />
                <StatCard icon={BarChart3} color="bg-sky-200" label={lang === "es" ? "Mundos activos" : "Active worlds"} value={overview.stats.filter((s) => s.done > 0).length} />
              </div>

              <div className="mt-8"><Badges stats={overview.stats} title={lang === "es" ? "Insignias ganadas" : "Badges earned"} /></div>
              <div className="mt-8"><Gallery readOnly /></div>
              <h2 className="font-fredoka text-2xl font-bold mt-10 text-slate-900">
                {t.parents.progress}
              </h2>
              <div className="mt-4 grid gap-3">
                {overview.stats.map((s) => (
                  <div key={s.module_id} className="bg-white border-2 border-slate-900 chunky-shadow-sm rounded-2xl p-4" data-testid={`parent-module-${s.module_id}`}>
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-slate-900">{s.title?.[lang] || s.title?.es}</span>
                      <span className="text-sm text-slate-700">
                        {s.done}/{s.total}
                      </span>
                    </div>
                    <div className="h-3 bg-slate-200 rounded-full mt-3 overflow-hidden border-2 border-slate-900">
                      <div
                        className="h-full bg-sky-400"
                        style={{ width: `${s.percent}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Show>

        <h2 className="font-fredoka text-2xl font-bold mt-12 text-slate-900">{t.parents.tipsTitle}</h2>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          {t.parents.tips.map((tip, i) => (
            <div key={i} className="bg-pink-100 border-2 border-slate-900 chunky-shadow-sm rounded-2xl p-5">
              <p className="font-bold text-slate-900">{tip}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value }) {
  return (
    <div className={`${color} border-2 border-slate-900 chunky-shadow-sm rounded-2xl p-5`}>
      <div className="flex items-center gap-3">
        <div className="bg-white border-2 border-slate-900 rounded-xl p-2">
          <Icon className="w-5 h-5 text-slate-900" />
        </div>
        <div>
          <p className="text-xs font-bold uppercase text-slate-700">{label}</p>
          <p className="font-fredoka text-3xl font-bold text-slate-900">{value}</p>
        </div>
      </div>
    </div>
  );
}
